/**
 * Admin: withdrawal limits per method (minimum, maximum, 24-hour limit per client), the wire / card fees,
 * and per crypto asset (USDT TRC20 / ERC20, BTC, ETH) the network fee in the coin and the minimum in USD.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import {
  fmtDateTime,
  parseWithdrawLimits,
  PAYOUT_CURRENCIES,
  WITHDRAWAL_ASSETS,
  WITHDRAWAL_METHODS,
  type FiatMethod,
  type WithdrawalAssetId,
  type WithdrawalMethod,
  type WithdrawLimits,
} from '@platform/shared';
import { cx, Spinner } from '../components/ui';
import { api, ApiError } from '../lib/api';

const input = 'h-9 w-full rounded-lg border border-line bg-bg px-3 text-[13px] text-fg outline-none focus:border-line-strong placeholder:text-muted';
const btn = 'h-8 rounded-md border border-line-strong px-3 text-xs font-semibold text-fg disabled:opacity-50';
const primary = 'h-9 rounded-lg bg-accent px-4 text-xs font-semibold text-white disabled:opacity-50';
const err = (e: unknown) => (e instanceof ApiError ? e.message : 'Something went wrong');

/** The form keeps every amount as typed text; empty max / 24-hour limit = no limit. */
type Row = { min: string; max: string; daily: string; fees?: Record<string, string>; assets?: Record<WithdrawalAssetId, { fee: string; min: string }> };
type Form = Record<WithdrawalMethod, Row>;

const toForm = (l: WithdrawLimits): Form => ({
  crypto: {
    min: l.crypto.min,
    max: l.crypto.max ?? '',
    daily: l.crypto.daily ?? '',
    assets: Object.fromEntries(WITHDRAWAL_ASSETS.map((a) => [a.id, { ...l.crypto.assets[a.id] }])) as Record<WithdrawalAssetId, { fee: string; min: string }>,
  },
  bank_wire: { min: l.bank_wire.min, max: l.bank_wire.max ?? '', daily: l.bank_wire.daily ?? '', fees: { ...l.bank_wire.fees } },
  card: { min: l.card.min, max: l.card.max ?? '', daily: l.card.daily ?? '', fees: { ...l.card.fees } },
});

const SUB: Record<WithdrawalMethod, string> = {
  crypto: 'USDT (TRC20, ERC20), BTC or ETH to a saved address. BTC and ETH are converted at the mark when requested. Each asset’s own minimum applies when it is higher than the minimum here.',
  bank_wire: 'SWIFT (USD), SEPA (EUR) and Faster Payments (GBP) to a bank account in the client’s name.',
  card: 'Refund to a card the client has deposited with.',
};

function Money({ label, value, onChange, placeholder, hint, unit }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; hint?: string; unit?: string }) {
  return (
    <label className="flex min-w-0 flex-col gap-1 text-[11px] text-muted">
      {label}
      <span className="relative">
        {unit ? (
          <span className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-xs text-muted">{unit}</span>
        ) : (
          <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-xs text-muted">$</span>
        )}
        <input inputMode="decimal" value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value.replace(/[^\d.]/g, ''))} className={cx(input, 'num', unit ? 'pr-12' : 'pl-6')} />
      </span>
      {hint && <span>{hint}</span>}
    </label>
  );
}

function Section({ title, sub, children }: { title: string; sub: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-3 rounded-xl border border-line bg-panel p-4">
      <div>
        <h2 className="m-0 text-sm font-semibold">{title}</h2>
        <p className="m-0 mt-0.5 text-xs text-muted">{sub}</p>
      </div>
      {children}
    </section>
  );
}

export function WithdrawLimitsTab() {
  const [data, setData] = useState<{ limits: WithdrawLimits; defaults: WithdrawLimits; updatedBy: string | null; updatedAt: string | null } | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [f, setF] = useState<Form | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const reload = useCallback(async () => {
    try {
      const d = await api.get<NonNullable<typeof data>>('/api/admin/withdrawal-limits');
      setData(d);
      setF(toForm(d.limits));
      setLoadErr(null);
    } catch (e) {
      setLoadErr(err(e));
    }
  }, []);
  useEffect(() => {
    void reload();
  }, [reload]);
  if (!f || !data) return loadErr ? <p className="text-sm text-down">{loadErr}</p> : <Spinner />;

  // Editing again clears the last save's message.
  const edit = (fn: (cur: Form) => Form) => {
    setMsg(null);
    setF((cur) => fn(cur!));
  };
  const set = (m: WithdrawalMethod, k: 'min' | 'max' | 'daily', v: string) => edit((cur) => ({ ...cur, [m]: { ...cur[m], [k]: v } }));
  const setFee = (m: FiatMethod, c: string, v: string) => edit((cur) => ({ ...cur, [m]: { ...cur[m], fees: { ...cur[m].fees, [c]: v } } }));
  const setAsset = (a: WithdrawalAssetId, k: 'fee' | 'min', v: string) =>
    edit((cur) => ({ ...cur, crypto: { ...cur.crypto, assets: { ...cur.crypto.assets!, [a]: { ...cur.crypto.assets![a], [k]: v } } } }));
  const parsed = parseWithdrawLimits(f);
  const dirty = JSON.stringify(f) !== JSON.stringify(toForm(data.limits));
  const atDefaults = JSON.stringify(f) === JSON.stringify(toForm(data.defaults));

  const save = async () => {
    if (!parsed.ok) return;
    setBusy(true);
    setMsg(null);
    try {
      await api.put('/api/admin/withdrawal-limits', { limits: parsed.limits });
      setMsg({ ok: true, text: 'Saved. New withdrawal requests use these limits now; requests already made are not changed.' });
      await reload();
    } catch (e) {
      setMsg({ ok: false, text: err(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 rounded-xl border border-line bg-panel/95 px-4 py-3 backdrop-blur">
        <div className="min-w-[260px] flex-[1_1_360px] text-xs">
          <b className="text-sm">Withdrawal limits</b>
          <div className="text-muted">
            Amounts are in USDT from the client’s balance. Leave a maximum or 24-hour limit empty for no limit.
            {data.updatedAt && ` Last changed ${fmtDateTime(data.updatedAt)}${data.updatedBy ? ` by ${data.updatedBy}` : ''}.`}
          </div>
        </div>
        {msg && <span className={cx('text-xs', msg.ok ? 'text-up' : 'text-down')}>{msg.text}</span>}
        {!parsed.ok && <span className="text-xs text-down">{parsed.error}</span>}
        <button type="button" className={btn} disabled={atDefaults || busy} onClick={() => edit(() => toForm(data.defaults))}>
          Reset to defaults
        </button>
        <button type="button" className={btn} disabled={!dirty || busy} onClick={() => edit(() => toForm(data.limits))}>
          Discard
        </button>
        <button type="button" className={primary} disabled={!dirty || !parsed.ok || busy} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Save changes'}
        </button>
      </div>

      {WITHDRAWAL_METHODS.map(({ id, label }) => (
        <Section key={id} title={label} sub={SUB[id]}>
          <div className="grid gap-3 sm:grid-cols-3">
            <Money label="Minimum per withdrawal" value={f[id].min} onChange={(v) => set(id, 'min', v)} />
            <Money label="Maximum per withdrawal" value={f[id].max} placeholder="No limit" onChange={(v) => set(id, 'max', v)} />
            <Money label="Per client, any 24 hours" value={f[id].daily} placeholder="No limit" onChange={(v) => set(id, 'daily', v)} hint="Pending, processing and paid requests count" />
          </div>
          {id === 'crypto' && (
            <>
              <h3 className="m-0 mt-1 text-xs font-semibold">By coin and network</h3>
              <div className="flex flex-col gap-3">
                {WITHDRAWAL_ASSETS.map((a) => (
                  <div key={a.id} className="grid items-start gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]">
                    <div className="pt-5 text-xs">
                      <b>{a.label}</b>
                      <div className="text-muted">Default fee {a.fee} {a.coin}, minimum ${a.min}</div>
                    </div>
                    <Money
                      label="Network fee"
                      unit={a.coin}
                      value={f.crypto.assets![a.id].fee}
                      onChange={(v) => setAsset(a.id, 'fee', v)}
                      hint={a.coin === 'USDT' ? 'In USDT, taken from the amount' : `In ${a.coin}, taken from what the client receives (up to ${a.dp} decimals)`}
                    />
                    <Money label="Minimum per withdrawal" value={f.crypto.assets![a.id].min} onChange={(v) => setAsset(a.id, 'min', v)} hint="In USD from the balance" />
                  </div>
                ))}
              </div>
            </>
          )}
          {id !== 'crypto' && (
            <>
              <h3 className="m-0 mt-1 text-xs font-semibold">Fee by payout currency</h3>
              <div className="grid gap-3 sm:grid-cols-3">
                {PAYOUT_CURRENCIES.map((c) => (
                  <Money key={c} label={`${c} payouts`} value={f[id].fees![c] ?? ''} onChange={(v) => setFee(id, c, v)} hint="In USDT, taken from the amount" />
                ))}
              </div>
            </>
          )}
        </Section>
      ))}
    </div>
  );
}
